import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, CreditCard, Activity, AlertCircle, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import JeaCard from "@/components/jea/JeaCard";
import Skeleton from "@/components/jea/Skeleton";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { centsToUSD, shortDate, relativeTime } from "@/lib/dates";

const StatCard = ({ label, value, sub, tone = "default", Icon, testid }) => {
  const valTone =
    tone === "danger" ? "text-danger"
    : tone === "warning" ? "text-warning"
    : tone === "success" ? "text-success"
    : "text-white";
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="rounded-[12px] bg-bg-secondary border border-border-default p-5"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <div className="jea-caption">{label}</div>
        {Icon ? <Icon size={16} className="text-text-muted" /> : null}
      </div>
      <div className={`mt-2 jea-mono font-extrabold text-[28px] ${valTone}`}>{value}</div>
      {sub && <div className="text-text-muted text-[11px] mt-1">{sub}</div>}
    </motion.div>
  );
};

const AdminOverviewPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/admin/overview");
        setData(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const t = data?.totals || {};

  return (
    <div className="space-y-6" data-testid="admin-overview-page">
      <div className="space-y-1">
        <div className="jea-caption">Platform</div>
        <h1 className="text-[32px] font-extrabold tracking-[-0.02em] text-white">
          Admin <span className="text-accent-pink">Overview</span>
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-[104px] rounded-[12px]" />)
        ) : (
          <>
            <StatCard
              label="Total Users" value={t.users || 0}
              sub={`${t.standard_users || 0} std / ${t.pro_users || 0} pro`}
              Icon={Users} testid="stat-total-users"
            />
            <StatCard
              label="Active Subscriptions" value={t.active_subscriptions || 0}
              sub={`${t.canceling_subscriptions || 0} canceling / ${t.past_due_subscriptions || 0} past due`}
              Icon={CreditCard} testid="stat-active-subs"
            />
            <StatCard
              label="Queries This Month" value={t.queries_this_month || 0}
              sub={`Revenue: ${centsToUSD(t.revenue_month_cents || 0)}`}
              Icon={Activity} testid="stat-queries"
            />
            <StatCard
              label="Pending Alerts" value={t.pending_alerts || 0}
              tone={t.pending_alerts > 0 ? "danger" : "success"}
              sub={t.pending_alerts > 0 ? "Requires action" : "All clear"}
              Icon={AlertCircle} testid="stat-alerts"
            />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <JeaCard data-testid="admin-recent-signups">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-white font-bold text-[16px]">Recent Signups</h2>
            <Link
              to="/admin/users"
              className="text-[12px] text-text-secondary hover:text-accent-pink inline-flex items-center gap-1"
              data-testid="link-view-all-users"
            >
              View All Users <ChevronRight size={12} />
            </Link>
          </div>
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : (data?.recent_signups || []).length === 0 ? (
            <div className="text-text-muted text-[13px]">No signups yet.</div>
          ) : (
            <div className="space-y-2">
              {(data?.recent_signups || []).map((u) => (
                <div key={u.id} className="p-3 rounded-[8px] bg-bg-primary border border-border-default flex items-center gap-3" data-testid={`recent-signup-${u.id}`}>
                  <div className="w-9 h-9 rounded-full bg-accent-pink text-[#091424] flex items-center justify-center font-bold text-[13px] shrink-0">
                    {(u.full_name || "U").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-white font-semibold text-[13px] truncate">{u.full_name}</div>
                    <div className="text-text-secondary text-[12px] truncate">{u.email}</div>
                  </div>
                  <span className="text-[10px] tracking-[0.08em] uppercase font-bold px-2 py-1 rounded-full bg-bg-tertiary text-text-secondary border border-border-default">
                    {u.account_type}
                  </span>
                  <div className="text-text-muted text-[11px] jea-date w-[110px] text-right">
                    {relativeTime(u.created_at)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </JeaCard>

        <JeaCard data-testid="admin-recent-revenue">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-white font-bold text-[16px]">Recent Revenue</h2>
            <Link
              to="/admin/reports"
              className="text-[12px] text-text-secondary hover:text-accent-pink inline-flex items-center gap-1"
              data-testid="link-view-full-ledger"
            >
              View Full Ledger <ChevronRight size={12} />
            </Link>
          </div>
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : (data?.recent_billing || []).length === 0 ? (
            <div className="text-text-muted text-[13px]">No revenue events yet.</div>
          ) : (
            <div className="space-y-2">
              {(data?.recent_billing || []).map((b) => (
                <div key={b.id} className="p-3 rounded-[8px] bg-bg-primary border border-border-default flex items-center gap-3" data-testid={`recent-revenue-${b.id}`}>
                  <div className="w-9 h-9 rounded-[8px] bg-accent-pink/10 border border-accent-pink/30 flex items-center justify-center shrink-0">
                    <CreditCard size={14} className="text-accent-pink" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-white text-[13px] font-medium truncate">{b.description}</div>
                    <div className="text-text-muted text-[11px] uppercase tracking-[0.06em] font-semibold">{b.kind}</div>
                  </div>
                  <div className="jea-mono text-accent-pink font-bold text-[14px]">{centsToUSD(b.amount_cents)}</div>
                  <div className="text-text-muted text-[11px] jea-date w-[110px] text-right">
                    {relativeTime(b.created_at)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </JeaCard>
      </div>
    </div>
  );
};

export default AdminOverviewPage;
